import React, { useState, useEffect, useMemo, useRef } from "react";
import { rtdb } from "../../config/firebase";
import { ref, onValue, remove } from "firebase/database";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";

import ModalDataCleaning from "./ModalDataCleaning";
import DetailDataCleaning from "./DetailDataCleaning";
import HeaderDataCleaning from "./HeaderDataCleaning";

import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";

import { exportToExcel } from "../../utils/exportToExcel";
import { printDataCleaningList } from "../../utils/printDataCleaning";

// =============================
// FIELD KONSTAN
// =============================
const ALL_PROFILE_FIELDS = [
  "nama", "nik", "npwp", "tempat", "tanggalLahir", "jenisKelamin",
  "agama", "pendidikan", "fotoIjazah", "pasFoto",
  "namaPerusahaan", "jabatan", "tanggalMasuk", "dplk", "bpjsKet", "bpjsKes",
  "alamat", "email", "noHp", "ket",
];

// =============================
// HITUNG KELENGKAPAN
// =============================
const calculateProfileCompletion = (profile) => {
  if (!profile) return 0;
  const filledCount = ALL_PROFILE_FIELDS.filter(
    (f) => profile[f] && profile[f].toString().trim() !== ""
  ).length;
  return (filledCount / ALL_PROFILE_FIELDS.length) * 100;
};

// =============================
// SKELETON CARD (SHIMMER)
// =============================
const SkeletonCard = () => (
  <div className="bg-white/70 rounded-2xl border border-gray-100 shadow-sm p-5 animate-pulse">
    <div className="flex items-center gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-gray-200" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
    </div>
    <div className="h-1.5 bg-gray-200 rounded-full" />
  </div>
);

// =============================
// PROGRESS BAR DENGAN PERSENTASE
// =============================
const ProgressBar = ({ percentage }) => {
  const rounded = Math.round(percentage);
  const color =
    rounded === 100
      ? "from-green-500 to-green-600"
      : rounded >= 50
      ? "from-yellow-400 to-yellow-500"
      : "from-red-400 to-red-500";

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500">Kelengkapan Data</span>
        <span
          className={`text-xs font-semibold ${
            rounded === 100
              ? "text-green-600"
              : rounded >= 50
              ? "text-yellow-500"
              : "text-red-500"
          }`}
        >
          {rounded}%
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <motion.div
          className={`bg-gradient-to-r ${color} h-1.5 rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${rounded}%` }}
          transition={{ duration: 0.8 }}
        />
      </div>
    </div>
  );
};

// =============================
// KOMPONEN UTAMA
// =============================
export default function DataCleaning() {
  const [dataCleaning, setDataCleaning] = useState([]);
  const [filter, setFilter] = useState("semua");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("semua");
  const [selectedGender, setSelectedGender] = useState("semua");
  const [editData, setEditData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const sentinelRef = useRef(null);

  // Ambil data
  useEffect(() => {
    const refDB = ref(rtdb, "anggota_cleaning/");
    const unsubscribe = onValue(refDB, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.keys(data).map((id) => ({ id, ...data[id] }));
      setDataCleaning(list);
      setTimeout(() => {
        setVisibleCount(20);
        setLoading(false);
      }, 600);
    });
    return () => unsubscribe();
  }, []);

  // Filter lokasi unik
  const locationOptions = useMemo(() => {
    const setLoc = new Set(dataCleaning.map((p) => p.namaPerusahaan).filter(Boolean));
    return ["semua", ...Array.from(setLoc).sort()];
  }, [dataCleaning]);

  // Filter utama
  const filteredDataForUI = useMemo(() => {
    let arr = [...dataCleaning];
    if (selectedLocation !== "semua")
      arr = arr.filter((p) => p.namaPerusahaan === selectedLocation);
    if (selectedGender !== "semua")
      arr = arr.filter((p) => p.jenisKelamin?.toLowerCase() === selectedGender);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      arr = arr.filter(
        (p) =>
          (p.nama && p.nama.toLowerCase().includes(q)) ||
          (p.nik && p.nik.includes(q))
      );
    }
    if (filter === "lengkap") arr = arr.filter((p) => calculateProfileCompletion(p) === 100);
    if (filter === "belumLengkap") arr = arr.filter((p) => calculateProfileCompletion(p) < 100);
    return arr;
  }, [dataCleaning, searchTerm, filter, selectedLocation, selectedGender]);

  const totalData = dataCleaning.length;
  const totalLengkap = dataCleaning.filter((p) => calculateProfileCompletion(p) === 100).length;
  const totalBelum = totalData - totalLengkap;

  // Infinite Scroll
  useEffect(() => {
    if (!sentinelRef.current || visibleCount >= filteredDataForUI.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && visibleCount < filteredDataForUI.length) {
          const prevScroll = window.scrollY;
          setLoading(true);
          setTimeout(() => {
            setVisibleCount((prev) => Math.min(prev + 20, filteredDataForUI.length));
            requestAnimationFrame(() => window.scrollTo({ top: prevScroll }));
            setLoading(false);
          }, 500);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [filteredDataForUI.length, visibleCount]);

  const visibleData = useMemo(
    () => filteredDataForUI.slice(0, visibleCount),
    [filteredDataForUI, visibleCount]
  );

  // CRUD Handler
  const handleAdd = () => setIsModalOpen(true);
  const handleEdit = (row) => { setEditData(row); setIsModalOpen(true); };
  const handleDetail = (row) => { setSelected(row); setIsDetailOpen(true); };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin hapus data ini?")) return;
    setIsDeleting(id);
    try {
      await remove(ref(rtdb, `anggota_cleaning/${id}`));
      toast.success("Data berhasil dihapus!");
    } catch {
      toast.error("Gagal menghapus data.");
    } finally {
      setIsDeleting(null);
    }
  };

  const handleSave = () => {
    setIsModalOpen(false);
    setEditData(null);
    toast.success("Data berhasil disimpan!");
  };

  const handleExportExcel = () => {
    if (filteredDataForUI.length === 0)
      return toast.error("Tidak ada data untuk diekspor!");
    const dataForExport = filteredDataForUI.map((p) => ({
      "Nama": p.nama || "-",
      "NIK": p.nik || "-",
      "Jenis Kelamin": p.jenisKelamin || "-",
      "Jabatan": p.jabatan || "-",
      "Perusahaan": p.namaPerusahaan || "-",
      "Tanggal Masuk": p.tanggalMasuk || "-",
    }));
    exportToExcel(
      dataForExport,
      `Laporan_Data_Cleaning_${new Date().toLocaleDateString("id-ID").replace(/\//g, "-")}`,
      "LAPORAN DATA ANGGOTA CLEANING SERVICE",
      "SONEY RAYA"
    );
    toast.success("Data berhasil diekspor ke Excel!");
  };

  const tabs = [
    { id: "semua", label: "Semua", count: totalData, color: "bg-gray-400" },
    { id: "lengkap", label: "Lengkap", count: totalLengkap, color: "bg-green-500" },
    { id: "belumLengkap", label: "Belum Lengkap", count: totalBelum, color: "bg-yellow-500" },
  ];

  return (
    <div className="flex flex-col h-full w-full p-4 md:p-6 bg-slate-50">
      <Toaster position="top-right" />

      <HeaderDataCleaning
        tabs={tabs}
        filter={filter}
        setFilter={setFilter}
        locationOptions={locationOptions}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        selectedGender={selectedGender}
        setSelectedGender={setSelectedGender}
        filteredDataForUI={filteredDataForUI}
        dataForReport={filteredDataForUI}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportExcel={handleExportExcel}
        printDataCleaningList={printDataCleaningList}
        handleAdd={handleAdd}
      />

      {/* GRID DATA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-4">
        {loading && visibleCount === 0 &&
          Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}

        <AnimatePresence>
          {visibleData.length > 0 &&
            visibleData.map((row) => {
              const completion = calculateProfileCompletion(row);
              const isComplete = completion === 100;
              return (
                <motion.div
                  key={row.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="group relative bg-white/60 backdrop-blur-lg rounded-2xl border border-white/50 shadow-md hover:shadow-xl transition"
                >
                  <div
                    className={`absolute top-4 right-4 h-3 w-3 rounded-full ${
                      isComplete ? "bg-green-400" : "bg-yellow-400"
                    }`}
                  />
                  <div className="p-5 flex-grow">
                    <div className="flex items-center gap-4 mb-4">
                      {row.pasFoto ? (
                        <img
                          src={row.pasFoto}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover shadow"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center text-xl font-bold text-white">
                          {row.nama?.charAt(0) || "?"}
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg">{row.nama}</h3>
                        <p className="text-xs text-gray-500">{row.nik}</p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <BriefcaseIcon className="w-4 h-4 text-gray-400" />
                        {row.jabatan || "-"}
                      </div>
                      <div className="flex items-center gap-2">
                        <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
                        {row.namaPerusahaan || "-"}
                      </div>
                    </div>

                    <ProgressBar percentage={completion} />
                  </div>

                  <div className="border-t border-white/50 p-3 flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => handleDetail(row)}
                      className="p-2 rounded-md text-gray-500 hover:bg-blue-100 hover:text-blue-600"
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEdit(row)}
                      className="p-2 rounded-md text-gray-500 hover:bg-yellow-100 hover:text-yellow-600"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(row.id)}
                      disabled={isDeleting === row.id}
                      className="p-2 rounded-md text-gray-500 hover:bg-red-100 hover:text-red-600 disabled:bg-gray-200"
                    >
                      {isDeleting === row.id ? (
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      ) : (
                        <TrashIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </motion.div>
              );
            })}
        </AnimatePresence>

        {/* Sentinel + indikator akhir */}
        {filteredDataForUI.length > 0 && <div ref={sentinelRef} className="col-span-full h-10" />}

        {loading && visibleCount > 0 && visibleCount < filteredDataForUI.length &&
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)}

        {!loading && visibleCount >= filteredDataForUI.length && filteredDataForUI.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="col-span-full flex items-center justify-center py-4 text-gray-500 text-sm"
          >
            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
            <span>Telah menampilkan seluruh data.</span>
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <ModalDataCleaning
            initialData={editData}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSave}
          />
        )}
        {isDetailOpen && (
          <DetailDataCleaning
            data={selected}
            onClose={() => setIsDetailOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
